import { useMutation, useQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import User from "../../../assets/images/user.png";
import Edit from "../../../assets/images/edit.png";
import BackButton from "../../../Components/BackButton";
import { showToast } from "../../../Components/Toast/toast";
import withModal from "../../../HOC/withModal";
import { usePageTitle } from "../../../Hooks/usePageTitle";
import { getAffiliateProfile, updateAffiliateProfile } from "../../../Services/Affiliate/Dashboard";
import useUserStore from "../../../Stores/UserStore";
import { isNullOrEmpty } from "../../../Utils/Utils";
import "./Profile.css";
import TextInput from "../../../Components/TextInput";
import CustomButton from "../../../Components/CustomButton";


const EditProfile = ({ showModal }) => {
  usePageTitle("Edit Profile");
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch profile data
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['affiliateProfile'],
    queryFn: getAffiliateProfile,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const updateProfileMutation = useMutation({
    mutationFn: updateAffiliateProfile,
    onSuccess: (response) => {
      if (response.data) {
        setUser(response.data);
      }
      showModal(
        "Successfully",
        "Profile has been updated successfully.",
        () => {
          navigate("/affiliate/profile");
        },
        "success"
      );
    },
    onError: (error) => {
      console.error("Failed to update profile", error);
      if (!isNullOrEmpty(error.errors?.full_name)) {
        showToast(error.errors.full_name[0], "error");
      } else if (!isNullOrEmpty(error.errors?.phone)) {
        showToast(error.errors.phone[0], "error");
      } else {
        showToast(error.message || "Failed to update profile", "error");
      }
    },
  });

  const { isPending } = updateProfileMutation;

  // Set image preview when profile data loads
  useEffect(() => {
    if (profileData?.image) {
      setImagePreview(`${imageBaseUrl}${profileData.image}`);
    }
  }, [profileData, imageBaseUrl]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        showToast("Please select a valid image file (JPEG, PNG, GIF)", "error");
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        showToast("Image size should be less than 5MB", "error");
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Set the file for form submission
      setSelectedImage(file);
    }
  };

  // Prepare initial phone value for PhoneInput
  const getInitialPhoneValue = () => {
    if (!profileData?.phone) return '';
    // If phone already starts with +, return as is
    if (profileData.phone.startsWith('+')) {
      return profileData.phone;
    }
    // Otherwise, prepend dial_code if available
    const dialCode = profileData.dial_code ? `+${profileData.dial_code}` : '';
    return dialCode ? `${dialCode}${profileData.phone}` : profileData.phone;
  };

  const handleEditProfileSubmit = (values) => {
    // Parse phone number to extract dial_code and country_code
    let parsedPhoneNumber = null;
    let phone = values.phone || '';
    let dial_code = '';
    let country_code = '';

    if (phone && phone.length > 3) {
      try {
        parsedPhoneNumber = parsePhoneNumber(phone);
        if (parsedPhoneNumber) {
          phone = parsedPhoneNumber.nationalNumber;
          dial_code = parsedPhoneNumber.countryCallingCode;
          country_code = parsedPhoneNumber.country || profileData?.country_code || '';
        }
      } catch (error) {
        console.error('Error parsing phone number:', error);
      }
    }

    // Create FormData
    const formData = new FormData();
    formData.append('full_name', values.full_name || '');
    formData.append('phone', phone);
    formData.append('dial_code', dial_code);
    formData.append('country_code', country_code);
    
    if (selectedImage) {
      formData.append('image', selectedImage);
    }

    updateProfileMutation.mutate(formData);
  };

  if (profileLoading) {
    return (
      <section className="profile-management">
        <div className="admin-content-header mb-4 d-flex gap-2">
          <BackButton />
          <h2 className="screen-title mb-0">Edit Profile</h2>
        </div>
        <div className="text-center py-5">
          <p className="text-muted">Loading profile...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="profile-management">
      <div className="admin-content-header mb-4 d-flex gap-2">
        <BackButton />
        <h2 className="screen-title mb-0">Edit Profile</h2>
      </div>
      <div className="admin-content-body rounded-20 p-4 p-lg-4 p-xxl-4 mb-4">
        <Row>
          <Col md={10} lg={8} xl={6} xxl={5}>
            <Row>
              <Col xs={12} className="mb-3 mb-xxl-5">
                <div className="admin-profile-img position-relative">
                  <img
                    src={imagePreview || User}
                    className="img-fluid ml-0 rounded-circle"
                    style={
                      {width: '160px', height: '160px'}
                    }
                    alt="Profile"
                  />

                  <div className="image-upload-overlay">
                    <label
                      htmlFor="image-upload"
                      className="upload-profile-btn"
                    >
                      <img className="edit-img" src={Edit} alt="" />
                    </label>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Formik
              initialValues={{
                full_name: profileData?.full_name || '',
                phone: getInitialPhoneValue(),
              }}
              onSubmit={handleEditProfileSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
                isSubmitting,
              }) => (
                <Form>
                  <Row>
                    <Col xs={12} className="mb-3 mb-lg-4 mb-xxl-4">
                      <TextInput
                        name="full_name"
                        type="text"
                        required
                        label="Full Name"
                        placeholder="Enter Full Name"
                        value={values.full_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.full_name && errors.full_name}
                        labelClassName="label-padding-left"
                      />
                    </Col>
                    <Col xs={12} className="mb-3 mb-lg-4 mb-xxl-4">
                      <label className="form-label label-padding-left mb-2">
                        Phone Number
                        <span className="text-danger">*</span>
                      </label>
                      <PhoneInput
                        international
                        defaultCountry={profileData?.country_code?.toUpperCase() || 'US'}
                        placeholder="Enter phone number"
                        value={values.phone}
                        onChange={(value) => {
                          setFieldValue('phone', value || '');
                        }}
                        onBlur={() => handleBlur({ target: { name: 'phone' } })}
                        className="mainInput"
                        withCountryCallingCode
                      />
                      {touched.phone && errors.phone && (
                        <div className="text-danger mt-1">{errors.phone}</div>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col xs={12}>
                      <CustomButton
                        type="submit"
                        text="Update"
                        loading={isPending}
                        loadingText="Updating..."
                        className="min-width-220"
                        disabled={isPending}
                      />
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default withModal(EditProfile);
